/**
 * Regroupement « élèves » / « étudiants alternants » d'un tuteur — règle unique
 * partagée par /tutor/contacts et le formulaire « Nouveau rendez-vous »
 * (entretien académique), pour que les deux écrans ne divergent jamais.
 *
 * Un étudiant alternant est :
 *  - RATTACHÉ : il a ce tuteur comme tuteur académique
 *    (`student_profiles.academic_tutor_id`, migration 0237) — seul cas éligible
 *    à un entretien académique côté serveur (`create_direct_rendezvous`) ;
 *  - À RATTACHER : alternant déclaré et relié à ce tuteur (tutor_links actif)
 *    mais qui n'a encore choisi personne comme tuteur académique. Sans ce cas
 *    ces étudiants restaient noyés dans « Mes élèves » et la liste d'entretien
 *    restait vide (aucun moyen de les rattacher depuis l'écran d'entretien).
 *
 * Un alternant qui a déjà choisi UN AUTRE tuteur académique reste dans les
 * élèves : sa relation avec ce tuteur est celle d'un élève (matières).
 */

import type { TutorLinkedStudent } from '@/lib/tutor/queries';
import type { AcademicMentee } from '@/lib/tutor/rendezvous';

export interface AlternantEntry {
  studentId: string;
  displayName: string;
  /** Alternant DÉCLARÉ par l'étudiant (true) ou élève basculé par son tuteur (false). */
  isHigherEd: boolean;
  /** Ce tuteur est son tuteur académique (entretiens possibles). */
  attached: boolean;
  /** tutor_links actif : sa fiche (progression, devoirs) est accessible. */
  linked: boolean;
}

export interface StudentGroups {
  /** Élèves actifs qui ne sont pas des étudiants alternants de ce tuteur. */
  pupils: TutorLinkedStudent[];
  /** Étudiants alternants : rattachés d'abord, puis « à rattacher ». */
  alternants: AlternantEntry[];
}

export function splitStudentsAndAlternants(
  students: TutorLinkedStudent[],
  mentees: AcademicMentee[],
): StudentGroups {
  const active = students.filter((s) => s.status === 'active');
  const activeIds = new Set(active.map((s) => s.student_id));
  const menteeIds = new Set(mentees.map((m) => m.studentId));

  const toAttach = active.filter(
    (s) => s.is_alternant && !s.academic_tutor_id && !menteeIds.has(s.student_id),
  );
  const toAttachIds = new Set(toAttach.map((s) => s.student_id));

  return {
    pupils: active.filter((s) => !menteeIds.has(s.student_id) && !toAttachIds.has(s.student_id)),
    alternants: [
      ...mentees.map((m) => ({
        studentId: m.studentId,
        displayName: m.displayName,
        isHigherEd: m.isHigherEd,
        attached: true,
        linked: activeIds.has(m.studentId),
      })),
      ...toAttach.map((s) => ({
        studentId: s.student_id,
        displayName: s.display_name,
        // Sans tuteur académique, seul un alternant déclaré peut être dans cet état
        // (un élève basculé par son tuteur a toujours academic_tutor_id = ce tuteur).
        isHigherEd: true,
        attached: false,
        linked: true,
      })),
    ],
  };
}
